/**
 * Booking API service
 * Handles all API calls related to slot bookings
 * Uses centralized axios instance with interceptors from api.js
 */

import api from './api';

/**
 * Book a consultation slot
 * @param {number} slotId - Availability slot ID to book
 * @param {string} userNotes - Optional notes from user
 * @returns {Promise} API response with booking details
 */
export const bookSlot = async (slotId, userNotes) => {
  console.log(`[API] Booking slot ${slotId}`, { userNotes });

  try {
    const response = await api.post('/bookings', {
      slot_id: slotId,
      user_notes: userNotes || ''
    });

    console.log('[API] Booking created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Error creating booking:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get user's bookings
 * @returns {Promise} API response with user's bookings
 */
export const getMyBookings = async () => {
  console.log('[API] Fetching my bookings');

  try {
    const response = await api.get('/bookings');
    console.log(`[API] Found ${response.data.data?.bookings?.length || 0} bookings`);
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching bookings:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get astrologer's bookings
 * @returns {Promise} API response with astrologer's bookings
 */
export const getAstrologerBookings = async () => {
  console.log('[API] Fetching astrologer bookings');

  try {
    const response = await api.get('/bookings/astrologer-bookings');
    console.log(`[API] Found ${response.data.data?.bookings?.length || 0} astrologer bookings`);
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching astrologer bookings:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Cancel a booking
 * @param {number} bookingId - Booking ID to cancel
 * @returns {Promise} API response
 */
export const cancelBooking = async (bookingId) => {
  console.log(`[API] Cancelling booking ${bookingId}`);

  try {
    const response = await api.put(`/bookings/${bookingId}/cancel`, {});
    console.log('[API] Booking cancelled successfully');
    return response.data;
  } catch (error) {
    console.error('[API] Error cancelling booking:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get booking details by ID
 * @param {number} bookingId - Booking ID
 * @returns {Promise} API response with booking details
 */
export const getBookingDetails = async (bookingId) => {
  console.log(`[API] Fetching booking details for ${bookingId}`);

  try {
    const response = await api.get(`/bookings/${bookingId}`);
    console.log('[API] Booking details retrieved');
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching booking details:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Complete a booking (mark as completed)
 * @param {number} bookingId - Booking ID
 * @returns {Promise} API response
 */
export const completeBooking = async (bookingId) => {
  console.log(`[API] Completing booking ${bookingId}`);

  try {
    const response = await api.put(`/bookings/${bookingId}/complete`, {});
    console.log('[API] Booking marked as completed');
    return response.data;
  } catch (error) {
    console.error('[API] Error completing booking:', error.response?.data || error.message);
    throw error;
  }
};
